import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, setAccessToken, type TokenResponse, type UserProfile } from './api';

interface AuthState {
  user: UserProfile | null;
  /** True until the initial silent refresh settles, so guards do not flash. */
  loading: boolean;
  signIn: (session: TokenResponse) => void;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * A non-secret hint that a session *might* exist.
 *
 * The refresh cookie is HttpOnly, so the app cannot see whether it holds one.
 * Without a hint, the only way to find out is to call `/auth/refresh` on every
 * page load — and that endpoint is on the strict auth rate-limit tier, because
 * it mints credentials. An anonymous visitor reading a handful of marketing
 * pages would therefore burn their own login budget and be locked out of
 * signing in for five minutes: a denial of service against our own users.
 *
 * This flag grants nothing. Forging it causes one refresh call that fails.
 * Clearing it costs a signed-in user one extra sign-in. It exists purely so
 * visitors who have never signed in do not spend requests discovering that.
 */
const SESSION_HINT = 'wave.session';

function hasSessionHint(): boolean {
  try {
    return window.localStorage.getItem(SESSION_HINT) === '1';
  } catch {
    // Storage can be unavailable in private modes or with cookies blocked.
    // Falling back to "assume a session" keeps such a user able to sign in.
    return true;
  }
}

function setSessionHint(present: boolean): void {
  try {
    if (present) {
      window.localStorage.setItem(SESSION_HINT, '1');
    } else {
      window.localStorage.removeItem(SESSION_HINT);
    }
  } catch {
    /* storage unavailable; the hint is optional by design */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  /**
   * Restore the session on load.
   *
   * The access token is deliberately not persisted, so a page refresh always
   * starts with none. What survives is the HttpOnly refresh cookie, which this
   * exchanges for a fresh access token — the user stays signed in without a
   * long-lived credential ever being readable by script.
   */
  useEffect(() => {
    let cancelled = false;

    // Nothing suggests a session, so do not spend an auth-tier request finding
    // out. This is the common case: every first-time visitor to every page.
    if (!hasSessionHint()) {
      setLoading(false);
      return;
    }

    void (async () => {
      try {
        const session = await api.refresh();
        if (!cancelled) {
          setAccessToken(session.access_token);
          setUser(session.user);
        }
      } catch {
        // The cookie is gone or expired. Clear the hint so subsequent page
        // loads stop retrying.
        if (!cancelled) {
          setAccessToken(null);
          setSessionHint(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback((session: TokenResponse) => {
    setAccessToken(session.access_token);
    setUser(session.user);
    setSessionHint(true);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      // Even if the call fails, drop local state — leaving a signed-out user
      // looking signed in is worse than a stale server-side session that will
      // expire on its own.
    } finally {
      setAccessToken(null);
      setUser(null);
      setSessionHint(false);
    }
  }, []);

  const value = useMemo(
    () => ({ user, loading, signIn, signOut }),
    [user, loading, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside an AuthProvider');
  }
  return context;
}
